import React, { useEffect, useState } from 'react';
import SideBar from '../components/SideBar';
import DetailsHeaderBar from '../components/DetailsHeaderBar';
import DetailsTopBar from '../components/DetailsTopBar';
import { useLocation, useNavigate } from 'react-router-dom';
import { advertisingAgentAPI } from '../services/advertisingAgentService';
import '../styles/AgentReferralHistory.css';

function InvitationHistory() {
  const location = useLocation();
  const agent = location.state?.agent;
  const navigate = useNavigate();

  const [invitations, setInvitations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (agent?.agent_id) {
      fetchReferralHistory();
    }
  }, [agent, currentPage]);

  const fetchReferralHistory = async () => {
    if (!agent?.agent_id) {
      setError('No agent ID available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const filters = {
        page: currentPage,
        per_page: 10
      };

      const response = await advertisingAgentAPI.getAgentReferralHistory(agent.agent_id, filters);
      console.log('Using Agent ID:', agent.agent_id);
      if (response.referrals) {
        setInvitations(response.referrals);
      }
    } catch (err) {
      setError('Failed to fetch referral history');
      console.error('Error fetching referral history:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!agent) {
    return <p style={{ padding: "30px" }}>No agent data available.</p>;
  }

  // Define all possible tabs
  const allTabs = [
    {
      key: "personal",
      label: "Personal Details",
      path: "/advertising-agent-details",
    },
    {
      key: "wallet",
      label: "Wallet Details",
      path: "/advertising-agent-wallet-details",
    },
    {
      key: "invitation",
      label: "Invitation Details",
      path: "/advertising-agent-invitation-details",
      showWhen: "approved",
    },
    {
      key: "request",
      label: "Request Details",
      path: "/advertising-agent-request-history",
    },
  ];

  // Filter tabs based on status
  const getTabsByStatus = (agentStatus) => {
    return allTabs.filter((tab) => {
      if (!tab.showWhen) return true;
      return tab.showWhen === agentStatus;
    });
  };

  const tabs = getTabsByStatus(agent.status);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Helper function to format location display
  const formatLocation = (invitation) => {
    if (invitation.country && invitation.region) {
      return `${invitation.country}, ${invitation.region}`;
    } else if (invitation.country) {
      return invitation.country;
    } else if (invitation.region) {
      return invitation.region;
    }
    return 'N/A';
  };

  // Get status class
  const getStatusClass = (status) => {
    const s = status?.toLowerCase() || 'pending';
    return s === 'verified' || s === 'approved' ? 'active' : 'pending';
  };

  const getStatusText = (status) => {
    const s = status?.toLowerCase() || 'pending';
    return s === 'verified' || s === 'approved' ? 'Active' : (status || 'Pending');
  };

  console.log('Using Agent ID:', agent.agent_id);
  const totalPages = Math.ceil(invitations.length / 10) || 1;

  return (
    <div className="member-details-layout">
      <SideBar />
      <div className="member-details-main">
        <DetailsHeaderBar
          title="Advertising Agent Details"
          subtitle="You can edit you details to upadate your profile"
          breadcrumbs={[
            { label: "Home", path: "/dashboard" },
            { label: "Manage Advertising Agent", path: "/manage-advertising-agent" },
            { label: "Advertising Agent Details" }
          ]}
        />
        
        <DetailsTopBar
          tabs={tabs}
          activeTab="invitation"
          agent={agent}
        />

        {/* Error Message */}
        {error && (
          <div style={{ 
            background: '#f8d7da', 
            color: '#721c24', 
            padding: '10px', 
            borderRadius: '4px', 
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            {error}
            <button 
              onClick={fetchReferralHistory} 
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '5px 10px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {/* {loading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '20px', 
            color: '#6c757d', 
            fontStyle: 'italic' 
          }}>
            Loading referral history...
          </div>
        )} */}

        <div className="wallet-container" style={{ minHeight: "600px", marginTop: "20px", marginLeft: "0", maxWidth: "100%" }}>
          <div className="referral-table" style={{ marginRight: "20px" }}>
            {/* <div className="table-header">
              <h2 style={{ fontSize: "24px", margin: "0 0 20px 0" }}>Referral History</h2>
            </div> */}
            
            <div className="table-content">
              <div className="table-responsive">
                <table className="user-table" style={{ width: "100%", margin: "0" }}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Joined Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.length > 0 ? (
                      invitations.map((invitation, index) => (
                        <tr key={invitation.id || index}>
                          <td>{(currentPage - 1) * 10 + index + 1}</td>
                          <td className="name-cell">
                            {invitation.name || `${invitation.first_name || ''} ${invitation.last_name || ''}`.trim() || 'N/A'}
                          </td>
                          <td>{invitation.email || 'N/A'}</td>
                          <td>{formatLocation(invitation)}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(invitation.status)}`}>
                              {getStatusText(invitation.status)}
                            </span>
                          </td>
                          <td>{formatDate(invitation.joined_date || invitation.created_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6">
                          <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
                            <div className="empty-icon" style={{ fontSize: '40px', marginBottom: '10px' }}>👤</div>
                            <h3>No Referrals Found</h3>
                            <p>This agent hasn't referred anyone yet.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && invitations.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination">
                    <button 
                      className="page-btn prev-next" 
                      onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    
                    <div className="page-numbers">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={i}
                            className={`page-btn ${currentPage === pageNum ? "active" : ""}`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button 
                      className="page-btn prev-next" 
                      onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvitationHistory;
